/**
 * createTableViewport.ts - Scrolls the table's cells into view and positions the floating cell editor
 * over the cell being edited.
 *
 * Owned by the table's interactions, which share their private {@link TableElements} registry with it.
 * Like the interactions, every method reads the latest model and props at call time, and nothing runs
 * once the table has unmounted.
 */

import $ from 'jquery';
import type {TableVirtuosoHandle} from 'react-virtuoso';

import {assert} from 'Asserts';
import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {setCellEditorPosition} from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {computeCellCid, computeCellIndexFromCid} from 'notebook/tables/store/logic/TableIdentityLogic';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableViewsModel} from 'notebook/tables/store/selectors/tableModel/TableViewsSelectors';
import type {TableProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';

/** How long a programmatic scroll animates for, and so how long a retry waits for one to settle. */
export const DRAG_SCROLL_DURATION = 200;
const DRAG_SCROLL_PADDING = 30;

/**
 * The table DOM the interactions and viewport read, kept current by the write-only
 * `interactions.attach` callbacks. Lives only inside the interactions' closure: it is never put on the
 * model or in store state, so attaching an element notifies no subscriber.
 */
export interface TableElements {
  /** The table's root element. */
  tableRoot: HTMLDivElement | null;
  focusBearer: HTMLDivElement | null;
  horizontalScrollContainer: HTMLDivElement | null;
  /** The page's vertical scroll container, found by `useTableMeasurement`. */
  pageVerticalScrollContainer: Element | null;
  stickyHeader: HTMLDivElement | null;
  virtualizationHandle: TableVirtuosoHandle | null;
}

/**
 * The last client coordinates of the pointer over the table's scroll area, since scroll events (which
 * also move a drag) carry no pointer position.
 */
export interface TablePointerPosition {
  clientX?: number;
  clientY?: number;
}

export interface TableViewport {
  /**
   * Positions the cell editor over the cell at `index`. When the cell is not rendered or is out of
   * view, scrolls it into view instead and retries once the scroll settles, while the edit is still
   * active and the editor has not been positioned.
   */
  setTableCellEditorPosition: (index: TableIndex) => void;
  /** Scrolls the table and page so the cell at `index` is visible below the sticky header. */
  scrollCellIntoView: (index: TableIndex) => void;
  /** Scrolls the page to center the table when it is entirely out of view. */
  scrollTableIntoView: () => void;
  /**
   * Clears the cell editor's position after an edit, first scrolling back to the edited cell when the
   * user scrolled away from it during the edit.
   */
  scrollCellIntoViewAfterEdit: () => void;
  /** Extends the in-progress drag to the cell left under the pointer by a scroll. */
  scrollCompleteHandler: () => void;
  queryCellElement: (index: TableIndex) => Element | null;
  /** The container that scrolls the table vertically: its own scroll area when height-limited. */
  getTableVerticalScrollContainer: () => HTMLElement | null;
  /**
   * `getTableVerticalScrollContainer` for the given `views`, so render can pass its subscribed
   * snapshot instead of reading the live model.
   */
  getTableVerticalScrollContainerForViews: (views: TableVerticalScrollViews) => HTMLElement | null;
  getPageVerticalScrollContainer: () => Element | null;
  getSingleInputGroupHeight: () => number;
  /** Cancels any scheduled retry. Called when the table unmounts. */
  cancelPendingWork: () => void;
}

export type TableVerticalScrollViews = Pick<TableViewsModel, 'isFixedHeight' | 'isMaximizedOrSplitScreen'>;

export interface TableViewportParams {
  elements: TableElements;
  pointerPosition: TablePointerPosition;
  getTableModel: () => TableModel;
  getTableProps: () => TableProps;
  setState: (state: Partial<TableEditableState>) => void;
  isMounted: () => boolean;
  /** Called with the (non-ghost) cell under the pointer when a scroll finishes during a drag. */
  onPointerOverCellAfterScroll: (index: TableIndex) => void;
}

/** Whether the footer and horizontal scrollbar stick to the bottom of the viewport. */
export function computeIsBottomContainerSticky({
  isFullHeight,
  viewer,
  showFooter,
}: {
  isFullHeight: boolean;
  viewer: boolean;
  showFooter: boolean;
}): boolean {
  // The persistent horizontal scrollbar shows whenever the table is not a viewer.
  return isFullHeight && (!viewer || showFooter);
}

export function createTableViewport({
  elements,
  pointerPosition,
  getTableModel,
  getTableProps,
  setState,
  isMounted,
  onPointerOverCellAfterScroll,
}: TableViewportParams): TableViewport {
  const pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  const schedule = (callback: () => void, delay: number): void => {
    const timeout = setTimeout(() => {
      pendingTimeouts.delete(timeout);
      if (isMounted()) {
        callback();
      }
    }, delay);
    pendingTimeouts.add(timeout);
  };

  const showsPersistentHorizontalScrollbar = (): boolean => !getTableProps().viewer;

  const isShowingCellEditor = (): boolean => {
    const {tableNamespace, table, fileId} = getTableProps();
    return SpreadsheetStore.getState(tableNamespace, table.name, fileId).cellEditorPosition != null;
  };

  const getHorizontalScrollContainer = (): HTMLElement | null => elements.horizontalScrollContainer;

  const getPageVerticalScrollContainer = (): Element | null => elements.pageVerticalScrollContainer;

  const getTableVerticalScrollContainerForViews = (views: TableVerticalScrollViews): HTMLElement | null => {
    if (views.isFixedHeight || views.isMaximizedOrSplitScreen) {
      return getHorizontalScrollContainer();
    }
    return (getPageVerticalScrollContainer() as HTMLElement | null) ?? null;
  };

  const getTableVerticalScrollContainer = (): HTMLElement | null =>
    getTableVerticalScrollContainerForViews(getTableModel().views);

  const getSingleInputGroupHeight = (): number =>
    elements.tableRoot?.querySelector('.mediocre-tableEditable-singleInputGroup')?.getBoundingClientRect()
      .height ?? 0;

  const queryCellElement = ([rowIndex, columnIndex]: TableIndex): Element | null => {
    const cellQuery = `tr td[data-cid='${computeCellCid(getTableProps().cid, rowIndex, columnIndex)}']`;
    const singleInputBoundaryIndex = getTableModel().singleInputs.boundaryIndex;
    return (
      elements.tableRoot?.querySelector(
        singleInputBoundaryIndex != null && columnIndex < singleInputBoundaryIndex
          ? `.mediocre-tableEditable-singleInputGroup-container ${cellQuery}`
          : `.mediocre-tableEditable-tableWrapper ${cellQuery}`
      ) ?? null
    );
  };

  const cellRect = (i: number, j: number): DOMRect | undefined => {
    const scrollArea = getHorizontalScrollContainer();
    if (!scrollArea) {
      return;
    }
    return $(scrollArea)
      .find(`[data-cid='${computeCellCid(getTableProps().cid, i, j)}']`)[0]
      ?.getBoundingClientRect();
  };

  const getStickyFooterSize = (): number => {
    const {views, visibility} = getTableModel();
    const isSticky = computeIsBottomContainerSticky({
      isFullHeight: views.isFullHeight,
      viewer: getTableProps().viewer ?? false,
      showFooter: visibility.showFooter,
    });
    if (!isSticky) {
      return 0;
    }
    return (
      TableLayoutConstants.TABLE_FOOTER_HEIGHT +
      (showsPersistentHorizontalScrollbar() ? TableLayoutConstants.NATIVE_SCROLLBAR_HEIGHT_ESTIMATE : 0)
    );
  };

  const scrollCompleteHandler = (): void => {
    if (!isMounted()) {
      return;
    }
    const {clientX, clientY} = pointerPosition;
    const {state, dimensions} = getTableModel();
    if (!(state.multiActive && clientX != null && clientY != null)) {
      return;
    }
    const el = document.elementFromPoint(clientX, clientY) as Element;
    const $cell = $(el).closest('.cell');
    if (!$cell.length) {
      return;
    }
    const index = computeCellIndexFromCid(getTableProps().cid, $cell.attr('data-cid'));
    if (index == null || !TableUtil.isIndexBetween(dimensions.firstIndex, dimensions.lastIndex, index)) {
      // Skip ghost cells
      return;
    }
    onPointerOverCellAfterScroll(index);
  };

  /** Scrolls by the given offsets, then extends any drag to the cell that ends up under the pointer. */
  const scrollBy = (offsetLeft: number, offsetTop: number): void => {
    if (!isMounted() || (offsetLeft === 0 && offsetTop === 0)) {
      return;
    }
    const horizontalScrollContainer = getHorizontalScrollContainer();
    const verticalScrollContainer = getTableVerticalScrollContainer();
    const animationOptions = {complete: scrollCompleteHandler, duration: DRAG_SCROLL_DURATION};

    // If they're the same container, then scroll together
    if (horizontalScrollContainer === verticalScrollContainer && horizontalScrollContainer) {
      $(horizontalScrollContainer)
        .stop()
        .animate(
          {
            scrollLeft: horizontalScrollContainer.scrollLeft + offsetLeft,
            scrollTop: horizontalScrollContainer.scrollTop + offsetTop,
          },
          animationOptions
        );
      return;
    }
    if (horizontalScrollContainer) {
      $(horizontalScrollContainer)
        .stop()
        .animate({scrollLeft: horizontalScrollContainer.scrollLeft + offsetLeft}, animationOptions);
    }
    if (verticalScrollContainer) {
      $(verticalScrollContainer)
        .stop()
        .animate({scrollTop: verticalScrollContainer.scrollTop + offsetTop}, animationOptions);
    }
  };

  const scrollCellIntoView = ([i, j]: TableIndex): void => {
    if (!isMounted()) {
      return;
    }
    const tableHorizontalScrollArea = getHorizontalScrollContainer();
    const tableHorizontalScrollAreaBox = tableHorizontalScrollArea?.getBoundingClientRect() ?? null;
    const pageVerticalScrollAreaBox = getPageVerticalScrollContainer()?.getBoundingClientRect() ?? null;
    const stickyHeader = elements.stickyHeader;
    const stickyHeaderBox = stickyHeader?.getBoundingClientRect() ?? null;

    if (!tableHorizontalScrollAreaBox || !pageVerticalScrollAreaBox) {
      return;
    }

    const {visibility, views, singleInputs, measurements, affixed} = getTableModel();
    const virtualizationHandle = elements.virtualizationHandle;
    if (virtualizationHandle && tableHorizontalScrollArea) {
      virtualizationHandle.scrollIntoView({
        index: i,
        calculateViewLocation: ({
          itemTop,
          itemBottom,
          viewportTop,
          viewportBottom,
          locationParams: {behavior, align, ...rest},
        }) => {
          const {isStickyHeaderVisible} = visibility;
          const {isMaximizedOrSplitScreen} = views;
          const singleInputGroupHeight = getSingleInputGroupHeight();
          const {stickyTopContentHeight} = measurements;

          /** A buffer of 1 base row-height to to trigger scroll for */
          const bufferHeight = TableLayoutConstants.TABLE_ROW_HEIGHT;

          /** The offset from the top to trigger scrolling from */
          let topOffset = 0;
          // don't add buffer for the first row
          if (i !== 0) {
            topOffset += bufferHeight;
          }
          if (isStickyHeaderVisible) {
            topOffset += stickyTopContentHeight;
          } else if (isMaximizedOrSplitScreen) {
            topOffset -= singleInputGroupHeight;
          }

          /** The offset from the bottom to trigger scrolling from */
          let bottomOffset = 0;
          // don't add buffer for the last row
          if (i !== getTableProps().table.table.length - 1) {
            bottomOffset += bufferHeight;
          }
          // Maximized tables include Single Inputs in the scroll area
          if (isMaximizedOrSplitScreen) {
            bottomOffset += singleInputGroupHeight;
          } else {
            bottomOffset += TableLayoutConstants.TABLE_FOOTER_HEIGHT;
            if (showsPersistentHorizontalScrollbar()) {
              bottomOffset += TableLayoutConstants.NATIVE_SCROLLBAR_HEIGHT_ESTIMATE;
            }
          }

          // Single Inputs should be stuck to the top of the window
          const isSingleInput = j < (singleInputs.boundaryIndex ?? 0);
          if (
            isSingleInput &&
            (isStickyHeaderVisible ||
              itemTop - singleInputGroupHeight < viewportTop ||
              itemTop - singleInputGroupHeight > viewportBottom)
          ) {
            return {
              ...rest,
              behavior,
              align: align ?? 'start',
              offset: -(singleInputGroupHeight + stickyTopContentHeight),
            };
          }
          if (itemTop < viewportTop + topOffset) {
            return {...rest, behavior, align: align ?? 'start', offset: -topOffset};
          }
          if (itemBottom > viewportBottom - bottomOffset) {
            return {...rest, behavior, align: align ?? 'end', offset: bottomOffset};
          }
          return null;
        },
      });
    }

    const cellBox = cellRect(i, j);
    // There is an edge case with inserting/deleting columns and rows where the selection updates out of band
    // with the table updating. Therefore, we make a best effort to perform the scroll
    if (cellBox == null) {
      return;
    }

    // Compute change in scrollLeft to horizontally scroll desired cell into view
    // If there are affixed columns, we want to scroll the cell into view relative to the affixed columns
    let tableBoxLeft = stickyHeaderBox ? stickyHeaderBox.left : tableHorizontalScrollAreaBox.left;

    // Don't scroll the current cell if it is affixed
    const cellIsAffixed = j < affixed.affixedColumnCount && affixed.showAffixedShadow;
    if (!cellIsAffixed) {
      if (stickyHeader) {
        // Not full screen
        // If there is a sticky header, then we want to scroll the cell into view relative to the rightmost
        // cell in the sticky header
        const lastAffixedHeaderCell = $(stickyHeader).find('.cell-rightShadow');
        if (lastAffixedHeaderCell.length) {
          tableBoxLeft = lastAffixedHeaderCell[0].getBoundingClientRect().right;
        }
      } else if (tableHorizontalScrollArea) {
        // Full screen
        // If there is no sticky header, then we want to scroll
        // to the rightmost cell that is sticky
        const lastAffixedColumnCell = $(tableHorizontalScrollArea).find(
          'tbody tr:first-child td.cell-rightShadow'
        );
        if (lastAffixedColumnCell.length) {
          tableBoxLeft = lastAffixedColumnCell[0].getBoundingClientRect().right;
        }
      }
    }

    let offsetLeft = 0;
    if (cellBox.left < tableBoxLeft) {
      offsetLeft = cellBox.left - tableBoxLeft - DRAG_SCROLL_PADDING;
    } else if (cellBox.right > tableHorizontalScrollAreaBox.right) {
      offsetLeft = cellBox.right - tableHorizontalScrollAreaBox.right + DRAG_SCROLL_PADDING;
    }

    // Compute change in scrollTop to vertically scroll desired cell into view
    // If there is a sticky header, we want to scroll the cell into view relative to the sticky header
    let offsetTop = 0;
    const bottomOfHeader = stickyHeaderBox?.bottom ?? pageVerticalScrollAreaBox.top;
    const bottomOfScroll = pageVerticalScrollAreaBox.bottom;
    const stickyFooterSize = getStickyFooterSize();
    const visibleViewHeight = bottomOfScroll - stickyFooterSize - bottomOfHeader;
    const cellTallerThanView = cellBox.height > visibleViewHeight;
    const cellTopAboveHeader = cellBox.top < bottomOfHeader;
    const cellBottomBelowFooter = cellBox.bottom > bottomOfScroll - stickyFooterSize;
    if (cellTopAboveHeader && !cellTallerThanView) {
      offsetTop = cellBox.top - bottomOfHeader - DRAG_SCROLL_PADDING;
    } else if (cellBottomBelowFooter && !cellTallerThanView) {
      offsetTop = cellBox.bottom - bottomOfScroll + stickyFooterSize + DRAG_SCROLL_PADDING;
    }

    scrollBy(offsetLeft, offsetTop);
  };

  const scrollTableIntoView = (): void => {
    const horizontalScrollAreaBox = getHorizontalScrollContainer()?.getBoundingClientRect();
    const verticalScrollArea = getPageVerticalScrollContainer();
    const verticalScrollAreaBox = verticalScrollArea?.getBoundingClientRect();
    if (!horizontalScrollAreaBox || !verticalScrollAreaBox) {
      return;
    }

    // If the table has been scrolled out of view, scroll it back centered
    if (
      horizontalScrollAreaBox.bottom < verticalScrollAreaBox.top ||
      horizontalScrollAreaBox.top > verticalScrollAreaBox.bottom
    ) {
      const verticalCenter = (verticalScrollAreaBox.top + verticalScrollAreaBox.bottom) / 2;
      const tableCenter = (horizontalScrollAreaBox.top + horizontalScrollAreaBox.bottom) / 2;
      verticalScrollArea?.scrollBy?.({top: tableCenter - verticalCenter, behavior: 'auto'});
    }
  };

  /** Retries positioning the editor once a scroll settles, if the edit is still waiting for it. */
  const retryPositioningAfter = (delay: number): void => {
    schedule(() => {
      const {multiSelIndex} = getTableModel().state;
      if (getTableProps().multiEditing && multiSelIndex && !isShowingCellEditor()) {
        setTableCellEditorPosition(multiSelIndex);
      }
    }, delay);
  };

  const setTableCellEditorPosition = (index: TableIndex): void => {
    if (!isMounted()) {
      return;
    }
    const [rowIndex, columnIndex] = index;
    const activeCell = queryCellElement(index);
    if (!activeCell) {
      scrollTableIntoView();
      schedule(() => {
        scrollCellIntoView(index);
      }, DRAG_SCROLL_DURATION);
      setCellEditorPosition(null);
      setState({hasScrolledSinceEdit: false, tableCellEditorIsLoading: true});
      retryPositioningAfter(DRAG_SCROLL_DURATION * 2); // Table scroll + cell scroll
      return;
    }
    const tableScrollContainerRect = getTableVerticalScrollContainer()?.getBoundingClientRect() ?? null;
    const activeCellRect = activeCell.getBoundingClientRect();
    const pageScrollContainerRect = getPageVerticalScrollContainer()?.getBoundingClientRect() ?? null;
    const stickyHeaderRect = elements.stickyHeader?.getBoundingClientRect() ?? null;
    const {state, singleInputs, visibility} = getTableModel();
    const headerRowHeight = state.rowHeights[0] ?? 0;
    // This may be a ghost cell, so we're not guaranteed to have a computed value yet
    const {computedTable} = getTableProps();
    const activeCellIsMerged = computedTable[rowIndex]?.[columnIndex]?.mergedBox !== undefined;
    assert(
      tableScrollContainerRect != null && pageScrollContainerRect != null,
      'Expected to find the horizontal and vertical scroll containers when starting an edit'
    );
    const topIsOutOfView = pageScrollContainerRect.top > activeCellRect.top;
    const bottomIsOutOfView = pageScrollContainerRect.bottom < activeCellRect.bottom;
    const leftIsOutOfView = tableScrollContainerRect.left > activeCellRect.left;
    const rightIsOutOfView = tableScrollContainerRect.right < activeCellRect.right;
    const activeCellIsTallerThanView = activeCellRect.height > pageScrollContainerRect.height;
    if (
      !state.tableCellEditorIsLoading &&
      !activeCellIsMerged &&
      // If the cell is taller than the view, we really only care about the top of the cell being in view
      // so don't scroll if the top is already visible
      !(activeCellIsTallerThanView && !topIsOutOfView) &&
      (topIsOutOfView || bottomIsOutOfView || leftIsOutOfView || rightIsOutOfView)
    ) {
      scrollCellIntoView(index);
      setCellEditorPosition(null);
      setState({hasScrolledSinceEdit: false, tableCellEditorIsLoading: true});
      retryPositioningAfter(DRAG_SCROLL_DURATION);
      return;
    }
    setCellEditorPosition(
      TableUtil.getTableCellEditorPosition(
        tableScrollContainerRect,
        activeCellRect,
        headerRowHeight,
        columnIndex < (singleInputs.boundaryIndex ?? 0),
        visibility.isStickyHeaderVisible,
        stickyHeaderRect
      )
    );
    setState({hasScrolledSinceEdit: false, tableCellEditorIsLoading: false});
  };

  const scrollCellIntoViewAfterEdit = (): void => {
    if (!isShowingCellEditor()) {
      return;
    }
    const {hasScrolledSinceEdit, multiSelIndex} = getTableModel().state;
    if (hasScrolledSinceEdit && multiSelIndex) {
      scrollCellIntoView(multiSelIndex);
      scrollTableIntoView();
      setCellEditorPosition(null);
      setState({hasScrolledSinceEdit: false});
    } else {
      // If they haven't scrolled, then just wipe the table cell editor position since we're done editing
      setCellEditorPosition(null);
    }
  };

  return {
    setTableCellEditorPosition,
    scrollCellIntoView,
    scrollTableIntoView,
    scrollCellIntoViewAfterEdit,
    scrollCompleteHandler,
    queryCellElement,
    getTableVerticalScrollContainer,
    getTableVerticalScrollContainerForViews,
    getPageVerticalScrollContainer,
    getSingleInputGroupHeight,
    cancelPendingWork: () => {
      for (const timeout of pendingTimeouts) {
        clearTimeout(timeout);
      }
      pendingTimeouts.clear();
    },
  };
}
